import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import ReactCountryFlag from 'react-country-flag';
import { exportAsExcel } from '../../Services/exportAsExcel';
import { fetchApi } from '../../Network/NetworkService';
import SimpleChart from '../Ui/SimpleChart';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import Select from 'react-select';
import toYMD from '../../Services/toYMD';
import { typeStates } from '../../Constants';
import isoDate from '../../Services/toIsoDate';
import 'react-calendar/dist/Calendar.css';

// Fallback French name for any ISO-2 country code, via the browser's native
// Intl API. The primary source is countryList.json (served by /requests/countryList);
// this only covers codes absent from that list (e.g. GG), or a failed fetch.
const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['fr'], { type: 'region' })
    : null;
const intlRegionLabel = (iso) => (regionNames ? regionNames.of(iso) : null) || iso;

// react-select menus render in a portal at high z-index so they escape the
// glassmorphism panels, whose backdrop-filter traps absolutely-positioned children.
const selectStyles = {
    option: (s) => ({ ...s, color: 'black' }),
    menuPortal: (s) => ({ ...s, zIndex: 9999 }),
};

// The 8 real Request state enum values (AGENTS.md: request states are an enum).
const STATE_OPTIONS = [
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'waiting', label: 'En attente' },
    { value: 'approved', label: 'Approuvé' },
    { value: 'toCall', label: 'À appeler' },
    { value: 'reviewed', label: 'Examiné' },
    { value: 'canceled', label: 'Annulé' },
    { value: 'refunded', label: 'Remboursé' },
    { value: 'terminated', label: 'Terminé' },
];

const typeLabel = (key) => {
    const known = typeStates.find((t) => t.value === key);
    return known ? known.label : key;
};

const fmtNumber = (value) => Number(value || 0).toLocaleString('fr-FR');

const defaultRangeDates = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    return [start, end];
};

const International = () => {
    // Committed server scope (date/type/state) + committed client-side country filter.
    const [filters, setFilters] = useState(() => {
        const [start, end] = defaultRangeDates();
        return { start: toYMD(start), end: toYMD(end), type: '', state: [] };
    });
    const [appliedPays, setAppliedPays] = useState('');
    // Draft values edited in the form; applied only on button click.
    const [draft, setDraft] = useState({ range: defaultRangeDates(), type: '', state: [], pays: '' });

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
    const [countryList, setCountryList] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (filters.start) params.set('start', isoDate(filters.start));
            if (filters.end) params.set('end', isoDate(filters.end));
            if (filters.type) params.set('types', filters.type);
            if (filters.state && filters.state.length) params.set('states', filters.state.join(','));
            const response = await fetchApi(`/international/requests-by-country?${params.toString()}`, 'GET');
            if (response.success) {
                setData(response.data);
            } else {
                setError(response.message || 'Impossible de charger les données.');
            }
        } catch (err) {
            setError(`Erreur lors du chargement : ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Country data comes from countryList.json via the requests controller —
    // one fetch, reused for the pays dropdown and every country label.
    useEffect(() => {
        fetchApi('/requests/countryList', 'GET')
            .then((res) => { if (res && res.success) setCountryList(res.countryLists || []); })
            .catch(() => { /* Intl fallback remains in place */ });
    }, []);

    const countryMap = useMemo(() => {
        const map = {};
        (countryList || []).forEach((c) => { map[c.value] = c.label; });
        return map;
    }, [countryList]);

    const countryLabel = useMemo(() => (iso) => countryMap[iso] || intlRegionLabel(iso), [countryMap]);

    const applyFilters = () => {
        setFilters({
            start: draft.range && draft.range[0] ? toYMD(draft.range[0]) : '',
            end: draft.range && draft.range[1] ? toYMD(draft.range[1]) : '',
            type: draft.type,
            state: draft.state || [],
        });
        setAppliedPays(draft.pays);
    };

    const resetFilters = () => {
        const range = defaultRangeDates();
        setDraft({ range, type: '', state: [], pays: '' });
        setFilters({ start: toYMD(range[0]), end: toYMD(range[1]), type: '', state: [] });
        setAppliedPays('');
    };

    const draftStart = draft.range && draft.range[0] ? toYMD(draft.range[0]) : '';
    const draftEnd = draft.range && draft.range[1] ? toYMD(draft.range[1]) : '';
    const [defaultStartDate, defaultEndDate] = defaultRangeDates();
    const periodChanged = draftStart !== toYMD(defaultStartDate) || draftEnd !== toYMD(defaultEndDate);
    // The period counts as a filter too, so changing ONLY the dates enables
    // "Réinitialiser" (and shows in the counter) instead of leaving it dead.
    const activeCount = (draft.type ? 1 : 0) + (draft.state && draft.state.length ? 1 : 0) + (draft.pays ? 1 : 0) + (periodChanged ? 1 : 0);
    const hasAnyFilter = activeCount > 0;
    const draftSnapshot = { start: draftStart, end: draftEnd, type: draft.type, state: draft.state, pays: draft.pays };
    const appliedSnapshot = { start: filters.start, end: filters.end, type: filters.type, state: filters.state, pays: appliedPays };
    const isDirty = JSON.stringify(draftSnapshot) !== JSON.stringify(appliedSnapshot);

    const columns = useMemo(() => {
        if (!data) return [];
        const known = typeStates.filter((t) => data.typeKeys.includes(t.value)).map((t) => t.value);
        const unknown = data.typeKeys.filter((k) => !typeStates.some((t) => t.value === k)).sort();
        return [...known, ...unknown];
    }, [data]);

    const countryOptions = useMemo(() => {
        // France is the home market and is excluded from this page entirely
        // (server-side), so the dropdown never offers it.
        const options = (countryList || [])
            .filter((c) => c.value !== 'FR')
            .map((c) => ({ value: c.value, label: c.label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
        if (appliedPays && !options.some((o) => o.value === appliedPays)) {
            options.push({ value: appliedPays, label: countryLabel(appliedPays) });
        }
        return options;
    }, [countryList, appliedPays, countryLabel]);

    const rows = useMemo(() => {
        if (!data) return [];
        let visible = data.rows.filter((r) => r.total > 0);
        if (appliedPays) visible = visible.filter((r) => r.country === appliedPays);
        const sorted = [...visible].sort((a, b) => {
            if (sortConfig.key === 'country') {
                const al = countryLabel(a.country);
                const bl = countryLabel(b.country);
                return sortConfig.direction === 'asc' ? al.localeCompare(bl, 'fr') : bl.localeCompare(al, 'fr');
            }
            const av = sortConfig.key === 'total' ? a.total : (a.types[sortConfig.key] || 0);
            const bv = sortConfig.key === 'total' ? b.total : (b.types[sortConfig.key] || 0);
            if (av === bv) return 0;
            return sortConfig.direction === 'asc' ? av - bv : bv - av;
        });
        return sorted;
    }, [data, appliedPays, sortConfig, countryLabel]);

    // Type columns with no requests in the currently visible rows are dropped so
    // a pays filter does not leave dead all-zero columns behind. The export uses
    // the same list — what you see is what you export. Declared AFTER `rows`,
    // which it reads (a hook reading a later declaration throws at render time).
    const visibleColumns = useMemo(() => {
        if (!data) return [];
        return columns.filter((c) => rows.some((r) => (r.types[c] || 0) > 0));
    }, [columns, rows]);

    // Totals for the VISIBLE rows (after the client-side pays filter), so the
    // totals row always reconciles with the rows above it.
    const filteredTotals = useMemo(() => {
        const byType = {};
        let all = 0;
        rows.forEach((r) => {
            all += r.total;
            Object.keys(r.types).forEach((k) => { byType[k] = (byType[k] || 0) + r.types[k]; });
        });
        return { byType, all };
    }, [rows]);

    // Share of the visible rows' total, for the muted percentage next to each Total.
    const sharePct = (value) => (filteredTotals.all ? Math.round((value / filteredTotals.all) * 1000) / 10 : 0);

    // Non-zero counts pop as filled indigo blocks, zeros stay muted, so the type
    // matrix reads as a hit pattern instead of a wall of identical numbers.
    const cellBg = (value) => (value > 0 ? 'rgba(var(--color-primary-rgb), 0.14)' : 'transparent');
    const cellText = (value) => (value > 0
        ? { color: 'var(--color-primary-dark)', fontWeight: 600 }
        : { color: 'var(--color-text-muted)' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const topCountry = useMemo(() => {
        if (!data) return null;
        let best = null;
        for (const r of data.rows) {
            if (r.total > 0 && (!best || r.total > best.total)) best = r;
        }
        return best ? countryLabel(best.country) : null;
    }, [data, countryLabel]);

    const chartData = useMemo(() => {
        if (!rows.length) return null;
        return {
            categories: rows.map((r) => countryLabel(r.country)),
            series: [{ name: 'Requêtes', data: rows.map((r) => r.total) }],
        };
    }, [rows, countryLabel]);

    const handleExport = () => {
        if (!data) return;
        const exportData = rows.map((r) => {
            const row = { 'Pays': countryLabel(r.country) };
            visibleColumns.forEach((c) => { row[typeLabel(c)] = r.types[c] || 0; });
            row['Total'] = r.total;
            return row;
        });
        const totalsRow = { 'Pays': 'Total' };
        visibleColumns.forEach((c) => { totalsRow[typeLabel(c)] = filteredTotals.byType[c] || 0; });
        totalsRow['Total'] = filteredTotals.all;
        exportData.push(totalsRow);
        exportAsExcel(exportData, 'international_requetes_pays');
    };

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>International</h2>
                    <p className="text-muted">
                        Nombre de requêtes par pays (hors France) — le pays est déduit du numéro de téléphone du patient.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
        )}

            {data && (
                <Row className="mb-4">
                    <Col xs={12} md={4}>
                        <Panel className="text-center">
                            <Panel.Body>
                                <h3 style={{ marginTop: 0 }}>{data.totals.all.toLocaleString('fr-FR')}</h3>
                                <div className="text-muted">Total requêtes</div>
                            </Panel.Body>
                        </Panel>
                    </Col>
                    <Col xs={12} md={4}>
                        <Panel className="text-center">
                            <Panel.Body>
                                <h3 style={{ marginTop: 0 }}>{rows.length}</h3>
                                <div className="text-muted">Pays avec requêtes</div>
                            </Panel.Body>
                        </Panel>
                    </Col>
                    <Col xs={12} md={4}>
                        <Panel className="text-center">
                            <Panel.Body>
                                <h3 style={{ marginTop: 0 }}>{topCountry || '—'}</h3>
                                <div className="text-muted">Top pays</div>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}


            <Row  className="mb-4">
                <Col xs={12}>
                    <Panel className="intl-filters">
                        <Panel.Heading>
                            <h6><i className="zmdi zmdi-filter-list" /> Filtres</h6>
                            <span className="right">
                                {hasAnyFilter ? `${activeCount} filtre${activeCount > 1 ? 's' : ''} actif${activeCount > 1 ? 's' : ''}` : 'Aucun filtre actif'}
                            </span>
                        </Panel.Heading>
                        <Panel.Body>
                            <Row>
                                <Col xs={12} sm={12} md={4}>
                                    <div className="form-group">
                                        <label><i className="zmdi zmdi-calendar" style={{ color: 'var(--color-text-muted)', marginRight: 6 }} />Période</label>
                                        <DateRangePicker
                                            onChange={(range) => setDraft({ ...draft, range })}
                                            value={draft.range}
                                            locale="fr-FR"
                                            format="dd/MM/y"
                                            dayPlaceholder="jj"
                                            monthPlaceholder="mm"
                                            yearPlaceholder="aaaa"
                                            maxDate={new Date()}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} md={4}>
                                    <div className="form-group">
                                        <label><i className="zmdi zmdi-file-text" style={{ color: 'var(--color-text-muted)', marginRight: 6 }} />Type de demande</label>
                                        <Select
                                            options={typeStates}
                                            value={typeStates.find((t) => t.value === draft.type) || null}
                                            onChange={(opt) => setDraft({ ...draft, type: opt ? opt.value : '' })}
                                            placeholder="Tous les types"
                                            isClearable
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} md={4}>
                                    <div className="form-group">
                                        <label><i className="zmdi zmdi-check-circle" style={{ color: 'var(--color-text-muted)', marginRight: 6 }} />Statut</label>
                                        <Select
                                            options={STATE_OPTIONS}
                                            value={STATE_OPTIONS.filter((s) => (draft.state || []).includes(s.value))}
                                            onChange={(opts) => setDraft({ ...draft, state: opts ? opts.map((o) => o.value) : [] })}
                                            placeholder="Tous les statuts"
                                            isMulti
                                            isClearable
                                            closeMenuOnSelect={false}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} sm={6} md={3}>
                                    <div className="form-group">
                                        <label><i className="zmdi zmdi-globe" style={{ color: 'var(--color-text-muted)', marginRight: 6 }} />Pays</label>
                                        <Select
                                            options={countryOptions}
                                            value={countryOptions.find((c) => c.value === draft.pays) || null}
                                            onChange={(opt) => setDraft({ ...draft, pays: opt ? opt.value : '' })}
                                            placeholder="Tous les pays"
                                            isClearable
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} md={9}>
                                    <div
                                        style={{
                                            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                                            paddingTop: 16,
                                            marginTop: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 12,
                                        }}
                                    >
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            Les filtres s'appliquent avec « Appliquer ».
                                        </div>
                                        <div>
                                            <Button
                                                bsStyle={isDirty ? 'primary' : 'default'}
                                                onClick={applyFilters}
                                                disabled={loading}
                                                style={{ marginRight: 10 }}
                                            >
                                                {isDirty && <i className="zmdi zmdi-refresh" />} Appliquer
                                            </Button>
                                            <Button onClick={resetFilters} disabled={!hasAnyFilter} style={{ marginRight: 10 }}>
                                                Réinitialiser
                                            </Button>
                                            <Button bsStyle="success" onClick={handleExport} disabled={!data || !rows.length}>
                                                Exporter Excel
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>


            {/* {chartData && (
                <Row  className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h6><i className="zmdi zmdi-chart" /> Requêtes par pays</h6>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart type="bar" height={320} categories={chartData.categories} series={chartData.series} />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )} */}

            <Row className="mb-4">
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <h6><i className="zmdi zmdi-view-list" /> Tableau des requêtes par pays et par type</h6>
                        </Panel.Heading>
                        <Panel.Body style={{ overflowX: 'auto' }}>
                            {loading && <div className="text-muted" style={{ padding: 20 }}>Chargement…</div>}
                            {!loading && data && rows.length === 0 && (
                                <div className="text-muted" style={{ padding: 20 }}>
                                    Aucune requête sur la période.
                                </div>
                            )}
                            {!loading && data && rows.length > 0 && (
                                <table className="table table-striped table-bordered table-hover" style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th
                                                onClick={() => handleSort('country')}
                                                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                title="Trier par pays"
                                                aria-sort={sortConfig.key === 'country' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                                            >
                                                Pays{' '}
                                                <span className={sortConfig.key === 'country' ? undefined : 'text-muted'}>
                                                    {sortConfig.key === 'country' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                </span>
                                            </th>
                                            {visibleColumns.map((c) => (
                                                <th
                                                    key={c}
                                                    onClick={() => handleSort(c)}
                                                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                    title={typeLabel(c)}
                                                    aria-sort={sortConfig.key === c ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                                                >
                                                    {typeLabel(c)}{' '}
                                                    <span className={sortConfig.key === c ? undefined : 'text-muted'}>
                                                        {sortConfig.key === c ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                    </span>
                                                </th>
                                            ))}
                                            <th
                                                onClick={() => handleSort('total')}
                                                style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                title="Trier par total"
                                                aria-sort={sortConfig.key === 'total' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                                            >
                                                Total{' '}
                                                <span className={sortConfig.key === 'total' ? undefined : 'text-muted'}>
                                                    {sortConfig.key === 'total' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.country}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <Button
                                                        bsStyle="link"
                                                        onClick={() => setDraft({ ...draft, pays: r.country })}
                                                        title={`Filtrer sur ${countryLabel(r.country)}`}
                                                        style={{ padding: 0, color: 'inherit' }}
                                                    >
                                                        <ReactCountryFlag
                                                            countryCode={r.country}
                                                            svg
                                                            title={countryLabel(r.country)}
                                                            style={{ fontSize: '1.4em', marginRight: 8, lineHeight: 1 }}
                                                        />
                                                        {countryLabel(r.country)}
                                                    </Button>
                                                </td>
                                                {visibleColumns.map((c) => {
                                                    const v = r.types[c] || 0;
                                                    return (
                                                        <td key={c} style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', background: cellBg(v) }}>
                                                            <span style={cellText(v)}>{fmtNumber(v)}</span>
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', background: cellBg(r.total) }}>
                                                    <strong style={cellText(r.total)}>{fmtNumber(r.total)}</strong>{' '}
                                                    <span className="text-muted" style={{ fontSize: 11, fontWeight: 'normal' }}>
                                                        ({sharePct(r.total).toLocaleString('fr-FR', { minimumFractionDigits: 1 })} %)
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="active">
                                            <td><strong>Total</strong></td>
                                            {visibleColumns.map((c) => (
                                                <td key={c} style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', background: cellBg(filteredTotals.byType[c] || 0) }}>
                                                    <strong style={cellText(filteredTotals.byType[c] || 0)}>{fmtNumber(filteredTotals.byType[c] || 0)}</strong>
                                                </td>
                                            ))}
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', background: cellBg(filteredTotals.all) }}>
                                                <strong style={cellText(filteredTotals.all)}>{fmtNumber(filteredTotals.all)}</strong>{' '}
                                                <span className="text-muted" style={{ fontSize: 11, fontWeight: 'normal' }}>(100 %)</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                            {!loading && data && rows.length > 0 && (
                                <div className="text-muted" style={{ marginTop: 10, fontSize: 12 }}>
                                    Les requêtes de France sont exclues. Celles dont le numéro de téléphone
                                    n'identifie aucun pays restent dans le total mais n'apparaissent dans aucune ligne.
                                </div>
                            )}
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default International;
